import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, Leaf, Map, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";

export default function AppShell({ children, hideNav = false, hideBottomNav = false, hideTopNav = false }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const showNav = user && !hideNav;
  // Bottom nav = mobile only. On md+ we surface the same tabs in the header.
  const showBottomNav = showNav && !hideBottomNav;
  // Top nav = desktop only. Independent from bottom-nav so `hideBottomNav`
  // (used by Home to avoid the mobile pill) does not also remove the
  // desktop tabs — the Corridor tab must remain reachable from every page.
  const showTopNav = showNav && !hideTopNav;

  return (
    <div className="app-shell grain">
      {/* Skip-to-content — keyboard-only affordance that lets users bypass
          the header/nav and land straight on the page's main content. */}
      {showNav && (
        <a href="#main-content" className="skip-link" data-testid="skip-to-content">
          {t("nav.skipToContent")}
        </a>
      )}
      {showNav && (
        <header className="flex items-center justify-between px-5 pt-5 pb-3 gap-4" data-testid="app-header">
          <button
            type="button"
            onClick={() => navigate("/home")}
            className="flex items-center gap-2 text-left min-w-0"
            aria-label={t("header.homeAria")}
          >
            <Leaf className="w-5 h-5 text-leaf shrink-0" strokeWidth={1.5} />
            <span className="font-display text-xl text-leaf-ink leading-tight">{t("app.name")}</span>
          </button>

          {/* Desktop-only inline tabs — the mobile bottom-nav mirrors these. */}
          {showTopNav && (
            <nav
              className="hidden md:flex items-center gap-1 bg-white/70 border border-[#E8E5DF] rounded-full px-1 py-1"
              data-testid="top-nav"
              aria-label={t("nav.primary")}
            >
              <TopNavTab to="/camera" icon={<Camera className="w-4 h-4" strokeWidth={1.5} />} label={t("nav.identify")} testId="top-nav-camera" />
              <TopNavTab to="/gallery" icon={<Leaf className="w-4 h-4" strokeWidth={1.5} />} label={t("nav.myPlants")} testId="top-nav-gallery" />
              <TopNavTab to="/corridor" icon={<Map className="w-4 h-4" strokeWidth={1.5} />} label={t("nav.corridor")} testId="top-nav-corridor" />
            </nav>
          )}

          <div className="flex items-center gap-3 shrink-0">
            <span
              className="text-[13px] uppercase tracking-[0.16em] font-semibold text-leaf-deep inline-flex items-center px-3 py-1.5 rounded-full bg-[#E6EFE9]"
              data-testid="state-pill"
            >
              {user.state}
            </span>
            <button
              onClick={() => navigate("/settings")}
              className="text-leaf hover:text-leaf-deep transition-colors"
              data-testid="settings-link"
              aria-label={t("header.settingsAria")}
            >
              <SettingsIcon className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </header>
      )}

      <main id="main-content" tabIndex={-1} className={showBottomNav ? "pb-8" : (hideNav ? "" : "pb-6")}>{children}</main>

      {showNav && <AppFooter />}

      {/* Bottom-nav spacer so the fixed pill doesn't cover the footer on mobile. */}
      {showBottomNav && <div className="h-24 md:hidden" aria-hidden="true" />}

      {showBottomNav && (
        <nav
          className="md:hidden fixed bottom-3 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[448px] bg-white/85 backdrop-blur-xl border border-[#E8E5DF] rounded-full shadow-botanical flex items-center justify-around px-2 py-2 z-30"
          data-testid="bottom-nav"
          aria-label={t("nav.primary")}
        >
          <NavTab to="/camera" icon={<Camera className="w-5 h-5" strokeWidth={1.5} />} label={t("nav.identify")} testId="nav-camera" />
          <NavTab to="/gallery" icon={<Leaf className="w-5 h-5" strokeWidth={1.5} />} label={t("nav.myPlants")} testId="nav-gallery" />
          <NavTab to="/corridor" icon={<Map className="w-5 h-5" strokeWidth={1.5} />} label={t("nav.corridor")} testId="nav-corridor" />
        </nav>
      )}

      {/* Persistent sr-only live region — created ONCE and left empty on
          mount so screen readers add it to the accessibility tree before
          any content is written. Transient components (MilestoneMoment,
          etc.) write into this via `announce()` from live-region.js.
          Without a pre-existing live region, most SR engines silently
          skip announcements whose live region is added together with
          its content. */}
      <div
        id="prairie-live-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="prairie-live-region"
      />

      {/* Global "back to top" button — appears once the user has scrolled far
          enough that reaching the top by hand would be tedious. Rendered
          inside AppShell so every authed view inherits it automatically. */}
      <ScrollToTop />
    </div>
  );
}

// Global footer — always available at the bottom of every authed page. Scrolls
// with content (not fixed) so it never overlaps modals, sticky panes, or the
// mobile bottom-nav (we insert a spacer above the nav so users can always
// scroll one more time to reach these links).
const AppFooter = () => {
  const { t } = useTranslation();
  return (
    <footer
      className="mt-10 pt-6 pb-10 md:pb-6 px-5 border-t border-[#E8E5DF]"
      data-testid="app-footer"
    >
      <ul className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs text-leaf-ink">
        <FooterLink to="/about" testId="footer-link-about">{t("settings.linkAbout")}</FooterLink>
        <span aria-hidden="true" className="text-leaf-mist">|</span>
        <FooterLink to="/terms" testId="footer-link-terms">{t("settings.linkTerms")}</FooterLink>
        <span aria-hidden="true" className="text-leaf-mist">|</span>
        <FooterLink to="/privacy" testId="footer-link-privacy">{t("settings.linkPrivacy")}</FooterLink>
      </ul>
    </footer>
  );
};

const FooterLink = ({ to, testId, children }) => (
  <li>
    <Link
      to={to}
      data-testid={testId}
      className="underline underline-offset-4 decoration-leaf/50 hover:decoration-leaf hover:text-leaf-deep transition-colors"
    >
      {children}
    </Link>
  </li>
);

const NavTab = ({ to, icon, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `flex-1 flex flex-col items-center gap-0.5 py-2 px-1 rounded-full transition-all ${
        isActive ? "text-leaf bg-[#E6EFE9]" : "text-leaf-mist hover:text-leaf"
      }`
    }
  >
    {icon}
    <span className="text-[10px] uppercase tracking-[0.15em] font-medium text-center leading-tight">{label}</span>
  </NavLink>
);

const TopNavTab = ({ to, icon, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] font-medium transition-colors ${
        isActive ? "bg-leaf text-white" : "text-leaf-ink hover:bg-parchment"
      }`
    }
  >
    {icon}
    {label}
  </NavLink>
);
